import {
  useEffect,
  useMemo,
  useRef,
  type ComponentProps,
  type ComponentType,
  type ReactNode,
} from 'react';
import Script from 'next/script';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { getPosthogContextProperties } from '../shared/posthogContext';
import { captureFirstTouchUtm } from './utmCapture';
import './types';

type AnalyticsProviderProps = {
  readonly children: ReactNode;
};

// PostHogProvider ignores unknown props at runtime (only destructures children/client/apiKey/options),
// so this cast just lets the root className rule attach to it without a wrapper DOM node.
type PostHogProviderRootProps = ComponentProps<typeof PostHogProvider> & {
  className?: string;
};

const PostHogProviderRoot = PostHogProvider as ComponentType<PostHogProviderRootProps>;

export function AnalyticsProvider({ children }: AnalyticsProviderProps): ReactNode {
  'use memo';

  const initializedRef = useRef(false);
  const measurementId = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;
  const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com';

  useEffect(() => {
    captureFirstTouchUtm();
  }, []);

  useEffect(() => {
    if (initializedRef.current || !posthogKey) {
      return;
    }

    posthog.init(posthogKey, {
      api_host: posthogHost,
      defaults: '2026-05-30',
      person_profiles: 'identified_only',
      capture_pageview: true,
      autocapture: true,
    });

    posthog.register(getPosthogContextProperties());

    initializedRef.current = true;
  }, [posthogHost, posthogKey]);

  // keep: PostHogProvider requires stable client ref
  const posthogContext = useMemo(() => ({ client: posthog }), []);

  return (
    <>
      {measurementId ? (
        <>
          <Script
            id="ga4-script"
            src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
            strategy="afterInteractive"
          />
          <Script id="ga4-init" strategy="afterInteractive">
            {`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = window.gtag || gtag;
gtag('js', new Date());
gtag('config', '${measurementId}', { send_page_view: true });`}
          </Script>
        </>
      ) : null}

      <PostHogProviderRoot className="analytics-provider" client={posthogContext.client}>
        {children}
      </PostHogProviderRoot>
    </>
  );
}
